// skybox.service.ts
import { Injectable, NgZone } from '@angular/core';
import * as THREE from 'three';
import * as mapboxgl from 'mapbox-gl';

@Injectable({ providedIn: 'root' })
export class SkyboxService {
  private renderer?: THREE.WebGLRenderer;
  private scene?: THREE.Scene;
  private camera?: THREE.PerspectiveCamera;
  private map?: mapboxgl.Map;
  private canvas?: HTMLCanvasElement;
  private disposables: Array<{ dispose(): void }> = [];

  private readonly onRender = () => this.sync();
  private readonly onResize = () => this.resize();

  constructor(private _ngZone: NgZone) { }

  attach(map: mapboxgl.Map): void {
    if (this.renderer) this.detach();

    const container = map.getContainer();
    const host = container.parentElement ?? container;

    const canvas = document.createElement('canvas');
    Object.assign(canvas.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      zIndex: '0',
      pointerEvents: 'none',
    });
    host.insertBefore(canvas, host.firstChild);

    // .mapboxgl-map is position:relative already, so this lands the map above us
    container.style.zIndex = '1';
    container.style.background = 'transparent';

    this.canvas = canvas;
    this.map = map;

    this._ngZone.runOutsideAngular(() => {
      this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false });
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0x04060e);

      this.camera = new THREE.PerspectiveCamera(36.87, 1, 0.1, 200);
      this.camera.rotation.order = 'YXZ';

      this.scene.add(this.buildStars(1400, 0.35, 1));
      this.scene.add(this.buildStars(400, 0.7, 0.55));

      this.resize();
      map.on('render', this.onRender);
      map.on('resize', this.onResize);
      this.sync();
    });
  }

  detach(): void {
    this.map?.off('render', this.onRender);
    this.map?.off('resize', this.onResize);
    this.disposables.forEach(d => d.dispose());
    this.disposables = [];
    this.renderer?.dispose();
    this.canvas?.remove();
    this.renderer = this.scene = this.camera = this.map = this.canvas = undefined as any;
  }

  private buildStars(count: number, size: number, opacity: number): THREE.Points {
    const radius = 80;
    const positions = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      const u = Math.random() * 2 - 1;
      const theta = Math.random() * Math.PI * 2;
      const r = Math.sqrt(1 - u * u);
      positions[i * 3] = radius * r * Math.cos(theta);
      positions[i * 3 + 1] = radius * u;
      positions[i * 3 + 2] = radius * r * Math.sin(theta);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: 0xffffff,
      size,
      sizeAttenuation: true,
      transparent: true,
      opacity,
      depthWrite: false,
    });

    this.disposables.push(geometry, material);
    return new THREE.Points(geometry, material);
  }

  /** Mirror Mapbox's camera orientation. Convention: -Z = north, +Y = zenith. */
  private sync(): void {
    if (!this.map || !this.renderer || !this.camera || !this.scene) return;

    const transform = (this.map as any).transform;
    const camera = this.camera;

    camera.fov = transform?.fov ?? 36.87;
    camera.rotation.y = -THREE.MathUtils.degToRad(this.map.getBearing());
    camera.rotation.x = THREE.MathUtils.degToRad(this.map.getPitch() - 90);
    camera.updateProjectionMatrix();

    this.renderer.render(this.scene, camera);
  }

  private resize(): void {
    if (!this.map || !this.renderer || !this.camera) return;
    const { clientWidth: w, clientHeight: h } = this.map.getCanvas();
    if (!w || !h) return;

    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }
}